import type { BlockId, ComponentTypeUnionType } from '@unifyapps/defs/types/block';
import { useEffect } from 'react';
import { COMPONENT_TYPES_WITH_AGGREGATION_METADATA } from '../components/DataSourceController/components/AggregationMetadataHandler/utils';
import { BlockLifecycleStatus, useGlobalStateStore } from '../stores/GlobalStateStore';
import { isEmptyBlock } from '../utils/block';

export default function useSetBlockLifecycle(
  blockId: BlockId,
  componentType: ComponentTypeUnionType,
  // Caller-supplied gate. When false the lifecycle transition is suppressed —
  // permission-denied / empty / invisible blocks must not register Staging
  // because dep-graph and aggregation counters observe these transitions.
  // Defaults to true to preserve behaviour for callers that haven't migrated.
  enabled = true,
) {
  const { setBlockLifecycle } = useGlobalStateStore().hooks.useActions();

  useEffect(() => {
    if (!enabled) return;
    if (isEmptyBlock(blockId)) return;

    // if the block is a component that has aggregation metadata, we need to set the lifecycle to staging
    // this is because lifecycle is not used for any other use case currently
    // perf optimisation - to reduce the number of times the lifecycle is set
    // if a use case comes to track for all the blocks, then we need to uplift the staging code at top level so that setting the lifecycle is less frequent
    if (COMPONENT_TYPES_WITH_AGGREGATION_METADATA.has(componentType)) {
      setBlockLifecycle(blockId, BlockLifecycleStatus.Staging);
    }

    return () => {
      if (COMPONENT_TYPES_WITH_AGGREGATION_METADATA.has(componentType)) {
        setBlockLifecycle(blockId, BlockLifecycleStatus.Unmounted);
      }
    };
  }, [blockId, componentType, setBlockLifecycle, enabled]);
}
